import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { searchActiveCourses, searchCourseProvisions } from '../../../store/courseService';
import { selectLocationIdsAndNamesLength } from '../../../store/dataSelectors';
import {
    loadAttendanceDetails,
    loadLocationIdsAndNames,
    loadProvisionStatusDetails
} from '../../../store/directusService';
import { todayDate } from '../../../utils/dateFunctions';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../utils/formValidation/commonConstants';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SELF_EMPLOYED_COACH,
    SUPERUSER
} from '../../../utils/userRoles';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';
import CurrentCourseManagement from '../currentCourse/CurrentCourseManagement';
import CurrentProvisionManagement from '../currentProvision/CurrentProvisionManagement';

import AllCourseTableRow from './AllCourseTableRow';
import AllProvisionTableRow from './AllProvisionTableRow';

import app from '../../../app.module.css';

const courseHeadCells = [
    { id: 'code', label: 'ID' },
    { id: 'name', label: 'Name' },
    { id: 'startDate', label: 'Start Date' },
    { id: 'endDate', label: 'End Date' },
    { id: 'numberOfModules', label: 'Modules' }
];

const initialActiveCoursesRowMetaData = {
    order: 'asc',
    orderBy: 'name',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const provisionHeadCells = [
    { id: 'code', label: 'ID' },
    { id: 'title', label: 'Name' }
];

const initialActiveProvisionsRowMetaData = {
    order: 'asc',
    orderBy: 'title',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const currentCoursesRoles = [...acceptedRoles, SELF_EMPLOYED_COACH];

const AllActiveCourseAndProvisionManagement = ({ isViewOnly = false, returnState }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [activeCourseSearchData, setActiveCourseSearchData] = useState({});
    const [activeProvisionSearchData, setActiveProvisionSearchData] = useState({});
    const [openActiveCourseId, setOpenActiveCourseId] = useState('');
    const [openActiveProvisionId, setOpenActiveProvisionId] = useState('');
    const [activeCoursesRowMetaData, setActiveCoursesRowMetaData] = useState(
        initialActiveCoursesRowMetaData
    );
    const [activeProvisionsRowMetaData, setActiveProvisionsRowMetaData] = useState(
        initialActiveProvisionsRowMetaData
    );
    const [isDisabled, setIsDisabled] = useState(true);
    const [isCurrentCoursesEditDisabled, setIsCurrentCoursesEditDisabled] = useState(true);
    const [isSelfEmployedCoach, setIsSelfEmployedCoach] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const locationIdsAndNamesLength = useSelector(selectLocationIdsAndNamesLength);
    const provisionStatusDetails = useSelector(
        (state) => state.entities.directusService.provisionStatusDetails
    );
    const attendanceDetails = useSelector(
        (state) => state.entities.directusService.attendanceDetails
    );

    const activeCourses = useSelector((state) => state.entities.courseService.activeCourses);
    const activeCoursesMetaData = useSelector(
        (state) => state.entities.courseService.activeCoursesMetaData
    );
    const loadingActiveCourses = useSelector(
        (state) => state.entities.courseService.loadingActiveCourses
    );
    const courseProvisions = useSelector((state) => state.entities.courseService.courseProvisions);
    const courseProvisionsMetaData = useSelector(
        (state) => state.entities.courseService.courseProvisionsMetaData
    );
    const loadingProvisions = useSelector(
        (state) => state.entities.courseService.loadingProvisions
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        attendanceDetails?.length < 1 && dispatch(loadAttendanceDetails());
        provisionStatusDetails?.length < 1 && dispatch(loadProvisionStatusDetails());
        locationIdsAndNamesLength < 1 && dispatch(loadLocationIdsAndNames());
    }, []);

    useEffect(() => {
        if (!currentParticipant?.id || roles?.length < 1) return;
        const disableOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);

        setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus || isViewOnly);
        setIsCurrentCoursesEditDisabled(
            !hasRole(currentCoursesRoles, roles) || disableOnClosedStatus || isViewOnly
        );
        setIsSelfEmployedCoach(hasRole([SELF_EMPLOYED_COACH], roles));
    }, [currentParticipant?.id, currentParticipant?.status, roles]);

    useEffect(() => {
        if (!('id' in currentParticipant)) return;

        setActiveCourseSearchData({
            active: true,
            serviceIds: [currentParticipant.serviceId],
            excludingParticipantId: currentParticipant.id,
            dateFrom: todayDate(),
            withAvailableCapacity: 1
        });

        setActiveProvisionSearchData({
            active: true,
            contractIds: [currentParticipant.contractId],
            excludingParticipantId: currentParticipant.id
        });
    }, [currentParticipant]);

    useEffect(() => {
        Object.keys(activeCourseSearchData).length > 0 &&
            dispatch(searchActiveCourses(activeCourseSearchData, 0));
        Object.keys(activeProvisionSearchData).length > 0 &&
            dispatch(searchCourseProvisions(activeProvisionSearchData, 0));
    }, [activeCourseSearchData, activeProvisionSearchData]);

    useEffect(() => {
        if (
            successMessage.startsWith('Module details for course') &&
            successMessage.endsWith('have been updated')
        ) {
            dispatch(searchActiveCourses(activeCourseSearchData, 0));
        } else if (successMessage === 'Participant has been submitted to provision') {
            dispatch(searchCourseProvisions(activeProvisionSearchData));
        }
    }, [successMessage]);

    // HELPER FNS

    const createActiveCourseRows = () =>
        stableSort(
            activeCourses,
            activeCoursesRowMetaData.orderBy,
            activeCoursesRowMetaData.order
        ).map((el) => (
            <AllCourseTableRow
                key={el.id}
                row={el}
                isDisabled={isDisabled}
                toggleDropdown={(id) => setOpenActiveCourseId(id)}
                openId={openActiveCourseId}
            />
        ));

    const createActiveProvisionRows = () =>
        stableSort(
            courseProvisions,
            activeProvisionsRowMetaData.orderBy,
            activeProvisionsRowMetaData.order
        ).map((el) => (
            <AllProvisionTableRow
                key={el.id}
                row={el}
                isDisabled={isDisabled}
                toggleDropdown={(id) => setOpenActiveProvisionId(id)}
                openId={openActiveProvisionId}
            />
        ));

    // RENDER
    return provisionStatusDetails?.length < 1 ? (
        <LoadingSpinner content="No current provision status found" />
    ) : (
        <div className={app.container} data-testid="active_courses_and_provisions">
            <div className={app.container}>
                <h2 className={app.sectionHeading}>All Active Courses</h2>
                {activeCoursesMetaData?.last && activeCourses?.length < 1 ? (
                    <LoadingSpinner content="No active course found" />
                ) : (
                    <ResultsTable
                        defaultOrderBy={activeCoursesRowMetaData.orderBy}
                        defaultRowsPerPage={activeCoursesRowMetaData.rowsPerPage}
                        apiParams={activeCourseSearchData}
                        headCells={courseHeadCells}
                        loadingValues={loadingActiveCourses}
                        loadResults={searchActiveCourses}
                        passRowMetaDataUp={setActiveCoursesRowMetaData}
                        tableRows={createActiveCourseRows()}
                        totalResults={activeCoursesMetaData?.totalElements}
                        metaData={activeCoursesMetaData}
                    />
                )}
            </div>
            <div className={app.divider} />

            <div className={app.container}>
                <h2 className={app.sectionHeading}>All Active Provisions</h2>
                {courseProvisionsMetaData?.last && courseProvisions?.length < 1 ? (
                    <LoadingSpinner content="No active provision found" />
                ) : (
                    <ResultsTable
                        defaultOrderBy={activeProvisionsRowMetaData.orderBy}
                        defaultRowsPerPage={activeProvisionsRowMetaData.rowsPerPage}
                        apiParams={activeProvisionSearchData}
                        headCells={provisionHeadCells}
                        loadingValues={loadingProvisions}
                        loadResults={searchCourseProvisions}
                        passRowMetaDataUp={setActiveProvisionsRowMetaData}
                        tableRows={createActiveProvisionRows()}
                        totalResults={courseProvisionsMetaData?.totalElements}
                        metaData={courseProvisionsMetaData}
                    />
                )}
            </div>

            <div className={app.divider} />
            <CurrentCourseManagement
                isDisabled={isCurrentCoursesEditDisabled}
                isSelfEmployedCoach={isSelfEmployedCoach}
            />
            <div className={app.divider} />
            <CurrentProvisionManagement isDisabled={isDisabled} returnState={returnState} />
        </div>
    );
};

AllActiveCourseAndProvisionManagement.propTypes = {
    participantId: PropTypes.string,
    isViewOnly: PropTypes.bool,
    returnState: PropTypes.object
};

export default AllActiveCourseAndProvisionManagement;
